import { IoArrowBack, IoCopyOutline, IoOpenOutline } from 'react-icons/io5';
import type { WhatsappTemplate } from '../types/whatsapp';
import WhatsappPreviewHeaderMedia from './WhatsappPreviewHeaderMedia';
import { WHATSAPP_COLORS as COLORS } from '../constants/whatsapp';
import { formatWhatsappText } from '../utils/formatWhatsappText';
import { renderTemplateBody } from '../utils/renderTemplateBody';

const FRAME =
  'rounded-xl border border-gray-300 overflow-hidden shadow-sm max-w-sm mx-auto';

interface WhatsappMessagePreviewProps {
  template: WhatsappTemplate | null;
  values: Record<string, string>;
  mediaUrl?: string | null;
  businessName?: string;
  emptyMediaLabels?: Record<string, string>;
}

export default function WhatsappMessagePreview({
  template,
  values,
  mediaUrl,
  businessName = 'ASSA',
  emptyMediaLabels,
}: WhatsappMessagePreviewProps) {
  if (!template) {
    return (
      <div className={FRAME}>
        <div
          className="flex h-96 items-center justify-center text-sm text-gray-400"
          style={{ backgroundColor: COLORS.chatBg }}
        >
          Pick a template to preview
        </div>
      </div>
    );
  }

  const body = formatWhatsappText(
    renderTemplateBody(template.body_text, values),
  );
  const time = new Date().toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div className={FRAME}>
      <div
        className="flex items-center gap-3 px-4 py-2.5"
        style={{ backgroundColor: COLORS.header }}
      >
        <span className="text-white opacity-90">
          <IoArrowBack size={20} />
        </span>
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/25 text-sm font-medium text-white">
          {businessName.charAt(0)}
        </div>
        <div className="leading-tight">
          <div className="text-sm font-medium text-white">{businessName}</div>
          <div className="text-xs text-white/70">business account</div>
        </div>
      </div>

      <div
        className="px-3 py-4"
        style={{ backgroundColor: COLORS.chatBg, minHeight: '22rem' }}
      >
        <div
          className="relative"
          style={{ maxWidth: '85%', marginLeft: '8px' }}
        >
          <span
            className="absolute top-0"
            style={{ left: '-8px', color: COLORS.bubble }}
          >
            <svg width="8" height="13" viewBox="0 0 8 13" aria-hidden="true">
              <path
                d="M1.533 3.568L8 12.193V1H2.812C1.042 1 .474 2.156 1.533 3.568z"
                fill="currentColor"
              />
            </svg>
          </span>

          <div
            className="overflow-hidden"
            style={{
              backgroundColor: COLORS.bubble,
              borderRadius: '7.5px',
              borderTopLeftRadius: 0,
              boxShadow: '0 1px 0.5px rgba(11, 20, 26, 0.13)',
            }}
          >
            <WhatsappPreviewHeaderMedia
              format={template.header_format}
              mediaUrl={mediaUrl}
              emptyLabels={emptyMediaLabels}
            />

            <div className="px-2 pb-1 pt-1.5">
              <p
                className="whitespace-pre-line"
                style={{
                  color: COLORS.text,
                  fontSize: '14.2px',
                  lineHeight: '19px',
                }}
              >
                {body}
              </p>
              <div
                className="text-right"
                style={{
                  color: COLORS.meta,
                  fontSize: '11px',
                  marginTop: '2px',
                }}
              >
                {time}
              </div>
            </div>

            {template.buttons.map((button) => (
              <div
                key={button.index}
                className="flex items-center justify-center gap-2 py-2.5 font-medium"
                style={{
                  color: COLORS.link,
                  fontSize: '14px',
                  borderTop: `1px solid ${COLORS.divider}`,
                }}
              >
                {button.sub_type === 'copy_code' ? (
                  <IoCopyOutline size={18} />
                ) : (
                  <IoOpenOutline size={18} />
                )}
                {button.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
